import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/leave/policy")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <ProtectedRoute requiredPermission="leave.type.manage">
      <LeavePolicyPage />
    </ProtectedRoute>
  );
}

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import PageHeader from "@/components/reusable-components/header/page-header";
import { DaySelector } from "@/components/crud/field-selector/day-selector";
import {
  useFetchLeavePolicy,
  useUpdateLeavePolicy,
  type EstaMode,
  type LeavePolicyPayload,
} from "@/hooks/api/leave/policy/useLeavePolicy";
import { formatError } from "@/lib/error";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DEFAULT_POLICY: LeavePolicyPayload = {
  working_weekdays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  esta_year_start_month: 1,
  esta_year_start_day: 1,
  esta_mode: "FRONTLOAD",
};

function LeavePolicyPage() {
  const policyQuery = useFetchLeavePolicy();
  const updatePolicy = useUpdateLeavePolicy();
  const [draft, setDraft] = useState<LeavePolicyPayload>(DEFAULT_POLICY);
  const [dayError, setDayError] = useState<string | null>(null);

  useEffect(() => {
    if (!policyQuery.data) return;
    const {
      working_weekdays,
      esta_year_start_month,
      esta_year_start_day,
      esta_mode,
    } = policyQuery.data;
    setDraft({
      working_weekdays,
      esta_year_start_month,
      esta_year_start_day,
      esta_mode,
    });
  }, [policyQuery.data]);

  const handleSave = async () => {
    const day = draft.esta_year_start_day;
    if (!Number.isInteger(day) || day < 1 || day > 28) {
      setDayError("Enter a day from 1 to 28.");
      return;
    }
    setDayError(null);
    try {
      await updatePolicy.mutateAsync(draft);
      toast.success("Leave policy saved");
    } catch (error) {
      toast.error(formatError(error, "Couldn't save the leave policy."));
    }
  };

  const disabled = policyQuery.isPending || updatePolicy.isPending;

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Leave Policy"
          description="Working week and earned sick time (ESTA) year settings."
          isLoading={disabled}
        >
          <Button onClick={handleSave} disabled={disabled}>
            {updatePolicy.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </PageHeader>

        {policyQuery.isPending ? (
          <Skeleton className="h-64 w-full rounded" />
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Working week</CardTitle>
                <CardDescription>
                  For employees with no shift roster, only these days count
                  toward a leave request. Rostered employees follow their
                  shifts.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DaySelector
                  label="Working days"
                  selectedDays={draft.working_weekdays}
                  onChange={(days) =>
                    setDraft((d) => ({ ...d, working_weekdays: days }))
                  }
                  disabled={disabled}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Earned sick time (ESTA)
                </CardTitle>
                <CardDescription>
                  When the benefit year starts, and whether hours are granted
                  up front or accrued as they are worked.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <div className="grid gap-2">
                  <Label>Year starts (month)</Label>
                  <Select
                    value={String(draft.esta_year_start_month)}
                    onValueChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        esta_year_start_month: Number(v),
                      }))
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((month, i) => (
                        <SelectItem key={month} value={String(i + 1)}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="esta_year_start_day">Year starts (day)</Label>
                  <Input
                    id="esta_year_start_day"
                    type="number"
                    min={1}
                    max={28}
                    value={draft.esta_year_start_day}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        esta_year_start_day: Number(e.target.value),
                      }))
                    }
                    disabled={disabled}
                  />
                  {dayError && (
                    <p className="text-xs text-red-500">{dayError}</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Mode</Label>
                  <Select
                    value={draft.esta_mode}
                    onValueChange={(v) =>
                      setDraft((d) => ({ ...d, esta_mode: v as EstaMode }))
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FRONTLOAD">
                        Front-load (full year up front)
                      </SelectItem>
                      <SelectItem value="ACCRUE">
                        Accrue (earned as hours are worked)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </SidebarInset>
  );
}
